import {
  CreateSegmentUserRequest,
  DeleteSegmentUserRequest,
} from '@taxfyle/api-internal/internal/feature_flag_pb'
import { GetCurrentUser } from 'iam/SessionStore'
import { collection } from 'infra/collection'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { FeatureFlagsAPI } from './API'
import FeaturePreview, { FeaturePreviewLocalStorage } from './FeaturePreview'
import { sessionStorage } from 'infra/storage'

const FEATURE_PREVIEW_PREFIX = 'featurePreview_'

export default class FeatureFlagStore {
  featurePreviews = collection<FeaturePreviewLocalStorage, FeaturePreview>({
    create: () => new FeaturePreview(),
  })

  constructor(
    private featureFlagsAPI: FeatureFlagsAPI,
    private getCurrentUser: GetCurrentUser
  ) {}

  private getLocalStorageFeatureKey(featureId: string) {
    return `${FEATURE_PREVIEW_PREFIX}${
      this.getCurrentUser().publicId
    }_${featureId}`
  }

  /**
   * Gets a feature preview from localStorage or from the collection if it's already loaded
   * @param featureId The feature flag id
   * @returns The feature preview info
   */
  getFeaturePreview(featureId: string): FeaturePreview {
    const featurePreview = this.featurePreviews.get(featureId)

    if (featurePreview) {
      return featurePreview
    }

    const featurePreviewItem = localStorage.getItem(
      this.getLocalStorageFeatureKey(featureId)
    )

    if (!featurePreviewItem) {
      return this.featurePreviews.set({ id: featureId, answered: false })
    }

    const featurePreviewJson: FeaturePreviewLocalStorage =
      JSON.parse(featurePreviewItem)

    return this.featurePreviews.set(featurePreviewJson)
  }

  /**
   * Adds the current user to the segment
   * @param segmentId The segment id
   */
  async addUserToSegment(segmentId: string) {
    const request = new CreateSegmentUserRequest().setSegmentId(segmentId)

    await this.featureFlagsAPI.addUserToSegment(request)
  }

  /**
   * Removes the current user from the segment
   * @param segmentId The segment id
   */
  async deleteUserFromSegment(segmentId: string) {
    const request = new DeleteSegmentUserRequest().setSegmentId(segmentId)

    await this.featureFlagsAPI.deleteUserFromSegment(request)
  }

  /**
   *
   * @param featureId Saves metadata related to the feature that's only valid for the current session
   * @param key
   * @param value
   */
  saveSessionMetadata(featureId: string, key: string, value: unknown) {
    const sessionStorageKey = `${key}_${this.getLocalStorageFeatureKey(
      featureId
    )}`

    sessionStorage.setItem(sessionStorageKey, value)
  }

  /**
   * Returns the session metadata value for the given feature and key
   * @param featureId
   * @param key
   * @returns
   */
  getSessionMetaData(featureId: string, key: string) {
    const sessionStorageKey = `${key}_${this.getLocalStorageFeatureKey(
      featureId
    )}`

    return sessionStorage.getItem(sessionStorageKey)
  }

  /**
   * Returns true if the feature is enabled
   * @param featureId
   * @param defaultValue
   * @returns
   */
  isFeaturePreviewEnabled(
    featureId: string,
    featurePreviewId: string,
    defaultValue: boolean
  ) {
    const toggleStore = getFeatureToggleClient()
    const featurePreviewEnabled = toggleStore.variation(featurePreviewId, false)

    if (featurePreviewEnabled) {
      const featurePreview = this.getFeaturePreview(featureId)

      if (!featurePreview.answered) {
        return defaultValue
      }
    }

    const featureEnabled = toggleStore.variation(featureId, false)

    return featureEnabled
  }
}
